import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  message,
  Typography,
  Upload,
  Row,
  Col,
  Divider,
} from "antd";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SketchPicker } from "react-color";
import { ExclamationCircleTwoTone, UploadOutlined } from "@ant-design/icons";
import logo from "../assets/logo.png";
import { API_BASE_URL } from "../api/api";

const { Title } = Typography;

const SetPasswordPage = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#6959a6");
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);

  useEffect(() => {
    if (!token) {
      message.error("Invalid or missing token.");
      navigate("/");
    }
  }, [token, navigate]);

  const handleImageUpload = (fileSetter, previewSetter) => (info) => {
    const file = info.file;
    if (file instanceof File) {
      previewSetter(URL.createObjectURL(file));
      fileSetter(file);
    }
  };

  const onFinish = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.warning("Passwords do not match.");
      return;
    }

    // Validate required image uploads
    if (!logoFile) {
      message.warning("Please upload a company logo.");
      return;
    }

    if (!faviconFile) {
      message.warning("Please upload a favicon.");
      return;
    }

    setLoading(true);

    try {
      // Build form data for password + appearance
      const formData = new FormData();
      formData.append("token", token);
      formData.append("password", values.password);
      formData.append("theme_color", primaryColor);
      formData.append("logo", logoFile);
      formData.append("favicon", faviconFile);

      const res = await fetch(`${API_BASE_URL}set_password`, {
        method: "POST",
        body: formData,
      });

      const json = await res.json();

      if (res.ok && json.success) {
        message.success("Setup complete. Redirecting to dashboard...");
        navigate("/");
      } else {
        message.error(json.error || "Failed to complete setup.");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #eef2f3, #8e9eab)",
        padding: 30,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: 520,
          maxWidth: "100%",
          background: "#fff",
          borderRadius: 18,
          padding: "36px 32px",
          boxShadow: "0 14px 40px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src={logo} alt="CRM Logo" style={{ width: 90 }} />
          <Title level={3} style={{ marginBottom: 0 }}>
            Set Your Account
          </Title>
          <p style={{ color: "#666" }}>Complete your account setup</p>
        </div>

        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item
            label="New Password"
            name="password"
            rules={[{ required: true, message: "Please enter a password" }]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[
              { required: true, message: "Please confirm your password" },
            ]}
          >
            <Input.Password placeholder="Confirm password" />
          </Form.Item>

          <Divider style={{ margin: "32px 0 20px" }}>Appearance</Divider>

          <Row gutter={24}>
            <Col span={12}>
              <Title level={5}>Primary Color</Title>
              <SketchPicker
                color={primaryColor}
                onChangeComplete={(color) => setPrimaryColor(color.hex)}
              />
              <div style={{ marginTop: 8 }}>
                Selected: <strong>{primaryColor}</strong>
              </div>
            </Col>

            <Col span={12}>
              <Title level={5}>
                Upload Logo <span style={{ color: "red" }}>*</span>
              </Title>
              <Upload
                showUploadList={false}
                maxCount={1}
                accept="image/*"
                beforeUpload={() => false}
                onChange={handleImageUpload(setLogoFile, setLogoUrl)}
              >
                <Button icon={<UploadOutlined />}>Upload Logo</Button>
              </Upload>
              <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                Recommended size: <strong>240×100px</strong> (wide logo)
              </div>
              {!logoUrl && (
                <div style={{ marginTop: 8, color: "red" }}>
                  <ExclamationCircleTwoTone twoToneColor="#faad14" /> Required
                </div>
              )}
              {logoUrl && (
                <div style={{ marginTop: 12 }}>
                  <img
                    src={logoUrl}
                    alt="Logo Preview"
                    style={{ maxHeight: 60 }}
                  />
                </div>
              )}

              <div style={{ marginTop: 24 }}>
                <Title level={5}>
                  Upload Favicon <span style={{ color: "red" }}>*</span>
                </Title>
                <Upload
                  showUploadList={false}
                  maxCount={1}
                  accept="image/*"
                  beforeUpload={() => false}
                  onChange={handleImageUpload(setFaviconFile, setFaviconUrl)}
                >
                  <Button icon={<UploadOutlined />}>Upload Favicon</Button>
                </Upload>
                <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                  A favicon is a small icon shown in the browser tab.
                  Recommended size: <strong>32×32px</strong>
                </div>
                {!faviconUrl && (
                  <div style={{ marginTop: 8, color: "red" }}>
                    <ExclamationCircleTwoTone twoToneColor="#faad14" /> Required
                  </div>
                )}
                {faviconUrl && (
                  <div style={{ marginTop: 12 }}>
                    <img
                      src={faviconUrl}
                      alt="Favicon Preview"
                      style={{ width: 32, height: 32 }}
                    />
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 32 }}>
            <Button type="primary" htmlType="submit" block loading={loading}>
              Complete Setup
            </Button>
          </Form.Item>
        </Form>

        <div
          style={{
            textAlign: "center",
            fontSize: 12,
            color: "#aaa",
            marginTop: 30,
          }}
        >
          © {new Date().getFullYear()} Sunbook.ca · All rights reserved
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
